import { HttpErrorResponse } from '@angular/common/http';
import { InfoDialog } from './../@shared/dialogs/info/info.component';
import { AuthenticationService } from '../@core/auth/authentication.service';
import { ResponseModel, ChangePasswordModel, Credentials, RecoveryDTO } from './../@core/models/login-models';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MEDIA_BREAKPOINTS } from '@app/@core/media.config';
import { CredentialsService } from '../@core/auth/credentials.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { PasswordErrorDialogComponent } from '@app/@shared/dialogs/password-error/password-error.component';
import { ResponseDialogComponent } from '@app/@shared/dialogs/response/response-dialog.component';
import { HOME, LOGIN } from '@app/@core/constants/routes.constants';
import { HeaderService } from '@app/@shared/header/header.service';

@Component({
  selector: 'app-password',
  templateUrl: './password.component.html',
  styleUrls: ['./password.component.scss'],
})
export class PasswordComponent implements OnDestroy, OnInit {
  USER_DISTINCT = 'User distinct';
  RECOVERY_TIME_EXPIRED = 'Recovery time expired';
  BAD_REQUEST = 'BAD_REQUEST';
  LOGIN_WITH_ACCOUNT = 'Inicia sesión con tu nueva contraseña para acceder a tu cuenta.';
  USER_NO_EXISTS = 'El usuario no existe.';
  HASH_EXPIRED = 'Tiempo de cambio de contraseña superado. Vuelve a solicitar el cambio de contraseña.';
  OLD_PASSWORD = 'Se está utilizando una contraseña antigua. Prueba con una nueva.';

  iconCircle: string;
  passwordForm: FormGroup;
  isMobile: boolean;
  name: string;
  isChangedPasswordFromHome: boolean;
  dataUser: Credentials = JSON.parse(localStorage.getItem('credentials'));

  //recovery
  isRecovery: boolean = false;
  hash: string;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private authenticationService: AuthenticationService,
    private breakpointObserver: BreakpointObserver,
    private credentialsService: CredentialsService,
    public dialog: MatDialog,
    private changePasswordService: HeaderService,
    private translate: TranslateService,
    private activatedRoute: ActivatedRoute
  ) {
    this.name = this.dataUser?.username;
    this.iconCircle = 'check_circle';
  }

  ngOnInit(): void {
    this.hash = this.activatedRoute.snapshot.params.hash;
    this.isChangedPasswordFromHome = this.changePasswordService.getChangePassword();
    if (this.hash && this.hash !== '') {
      this.isRecovery = true;
    }
    this.createForm();
    this.breakpointObserver
      .observe([
        MEDIA_BREAKPOINTS.XS,
        MEDIA_BREAKPOINTS.SM,
        MEDIA_BREAKPOINTS.MD,
        MEDIA_BREAKPOINTS.LG,
        MEDIA_BREAKPOINTS.XL,
      ])
      .subscribe((state: BreakpointState) => {
        this.isMobile = state.breakpoints[MEDIA_BREAKPOINTS.XS];
      });
  }

  ngOnDestroy(): void {
    this.isChangedPasswordFromHome = true;
  }

  get password() {
    return this.passwordForm.get('password');
  }

  get usuario() {
    return this.password.get('usuario');
  }

  get confirmPassword() {
    return this.password.get('confirmPassword');
  }

  private createForm() {
    this.passwordForm = this.formBuilder.group(
      {
        usuario: ['', Validators.required],
        currentPassword: ['', Validators.required],
        password: [
          '',
          [
            Validators.required,
            Validators.minLength(6),
            Validators.maxLength(6),
            hasANumber,
            hasAnAlphabeticChar,
            hasSpecialChar,
            isAllSameSequence,
            isAnOrderedSequence,
          ],
        ],
        confirmPassword: ['', Validators.required],
      },
      {
        validators: [isSimilarToUserValidation(), isSimilarToPassword()],
      }
    );
    this.passwordForm.patchValue({ usuario: this.name });

    if (this.isRecovery) {
      this.passwordForm.controls.currentPassword.disable();
    }
  }

  enviarFormulario() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '90%';
    dialogConfig.maxWidth = '450px';
    if (this.isRecovery) {
      const recovery: RecoveryDTO = {
        user: this.passwordForm.value.usuario,
        password: this.passwordForm.value.password,
        hexPasswordRecoveryDTO: this.hash,
      };
      new Promise((resolve, reject) => {
        this.authenticationService
          .passwordRecovery(recovery)
          .toPromise()
          .then(
            (response) => {
              if (response) {
                dialogConfig.data = {
                  text: this.translate.instant('dialog.password.login'),
                  icon: 'icon-ic_circle_check',
                  noRedirect: true,
                };
                const dialog = this.dialog.open(ResponseDialogComponent, dialogConfig);
                dialog.afterClosed().subscribe(() => {
                  this.router.navigate([LOGIN], { replaceUrl: true });
                });
              }
            },
            (error) => {
              let dialogText;
              let noRedir;
              if (error.error.message.includes(this.USER_DISTINCT)) {
                dialogText = this.translate.instant('dialog.password.userNotExists');
                noRedir = true;
              } else if (error.error.message.includes(this.RECOVERY_TIME_EXPIRED)) {
                dialogText = this.translate.instant('dialog.password.expiredHash');
                noRedir = false;
              } else if (error.error.message.includes(this.BAD_REQUEST)) {
                dialogText = this.translate.instant('matcard.warning');
                noRedir = true;
              }
              dialogConfig.data = { text: dialogText, icon: 'icon-ic_circle_cross', noRedirect: noRedir };
              this.dialog.open(ResponseDialogComponent, dialogConfig);
            }
          )
          .catch((error) => {
            console.log(error);
          });
      });
    } else {
      const changePassword: ChangePasswordModel = {
        username: this.passwordForm.value.usuario,
        password: this.passwordForm.value.password,
        oldPassword: this.passwordForm.value.currentPassword,
      };
      new Promise((resolve, reject) => {
        this.authenticationService
          .changePassword(changePassword)
          .toPromise()
          .then(
            (result: ResponseModel) => {
              if (result && result.descriptionResponse === 'OK') {
                this.dataUser.changePassword = false;
                this.credentialsService.setCredentials(this.dataUser, true);
                this.showDialogSucced(this.translate.instant('dialog.password.succeeded'));
                this.router.navigate(['/home'], { replaceUrl: true });
              }
            },
            () => {
              dialogConfig.maxWidth = '600px';
              dialogConfig.maxHeight = '374px';
              dialogConfig.width = '100%';
              dialogConfig.height = '100%';
              const errorDialog = this.dialog.open(PasswordErrorDialogComponent, dialogConfig);
              errorDialog.afterClosed().subscribe((dataDialogPass: any) => {
                if (dataDialogPass && dataDialogPass?.logout) {
                  this.cancelarFormulario();
                }
              });
            }
          );
      });
    }
  }

  cancelarFormulario() {
    if (this.isChangedPasswordFromHome) {
      this.router.navigate([HOME], {
        replaceUrl: true,
      });
    } else {
      this.authenticationService.logout().subscribe();
      this.router.navigate([LOGIN], {
        replaceUrl: true,
      });
    }
  }

  private showDialogSucced(err: string) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '90%';
    dialogConfig.maxWidth = '450px';
    dialogConfig.panelClass = '.faction-dialog';
    dialogConfig.data = { text: err, icon: 'icon-ic_circle_check', noRedirect: true };
    this.dialog.open(ResponseDialogComponent, dialogConfig);
  }
}

function hasANumber(c: AbstractControl): { [key: string]: boolean } | null {
  const regex = new RegExp('\\d');
  return c.pristine || (c && !regex.test(c.value)) ? { numberInvalid: true } : null;
}

function hasAnAlphabeticChar(c: AbstractControl): { [key: string]: boolean } | null {
  const regex = new RegExp('[a-zA-Z]');
  return c.pristine || (c && !regex.test(c.value)) ? { alphabetInvalid: true } : null;
}

function hasSpecialChar(c: AbstractControl): { [key: string]: boolean } | null {
  const regex = new RegExp('[!@#$%&*)(+,._-]');
  return c.pristine || (c && !regex.test(c.value)) ? { specialInvalid: true } : null;
}

function isAllSameSequence(c: AbstractControl): { [key: string]: boolean } | null {
  const regex = new RegExp('^(?!^(.)\\1*$)');
  return c.pristine || c.value === '' || c.value === null || (c && !regex.test(c.value))
    ? { similarInvalid: true }
    : null;
}

function isSimilarToUserValidation(): ValidatorFn {
  return (c: AbstractControl): ValidationErrors | null => {
    const usuario = c.get('usuario');
    const password = c.get('password');
    let regex = new RegExp(usuario.value);

    return password.pristine || (usuario && password && regex.test(password.value)) ? { usuarioInvalid: true } : null;
  };
}

function isSimilarToPassword(): ValidatorFn {
  return (c: AbstractControl): ValidationErrors | null => {
    const password = c.get('password');
    const confirmedPassword = c.get('confirmPassword');
    return password.pristine ||
      confirmedPassword.pristine ||
      (password && confirmedPassword && password.value !== confirmedPassword.value)
      ? { confirmedInvalid: true }
      : null;
  };
}

function isAnOrderedSequence(c: AbstractControl): { [key: string]: boolean } | null {
  return c.pristine || (c && isOrdered(c.value)) ? { sequenceInvalid: true } : null;
}

function isOrdered(text: string) {
  let n = text.length;
  for (let i = 1; i < n; i++) {
    if (text[i] < text[i - 1]) return false;
  }
  return true;
}
